import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch } from '../../store';
import {
  assignOrganization,
  createOrganization,
} from '../../store/actions/OnboardingActions';
import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';

import TagInput from '../shared/TailwindComponents/TagInput';
import { Well } from '../shared/brand/Field';
import FieldShell, { FOCUS_RING_ONLY } from '../shared/TailwindComponents/FieldShell';
import { FormActions } from '../shared/FormComponents';
import { getAuth0Email } from '../../auth0-integration/utils/auth0EmailUtils';
import {
  PiCheckCircleLight,
  PiCaretDownLight,
  PiCaretRightLight,
} from 'react-icons/pi';
import OnboardingStepCard from './OnboardingStepCard';

const OrganizationCheckStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user: auth0User } = useAuth0();
  const { t } = useTranslation();
  const onboarding = useSelector((state: RootState) => state.onboarding);
  const domainInitializedRef = useRef(false);
  const [organizationData, setOrganizationData] = useState({
    name: '',
    type: 'law_firm',
    email_domains: [] as string[],
    org_details: {
      regions: [] as string[],
      description: '',
    },
    active_license_count: 1,
  });
  const [showOrgDetails, setShowOrgDetails] = useState(false);

  const realEmail = getAuth0Email(auth0User);
  const domainFromEmail = realEmail?.split('@')[1] || '';

  useEffect(() => {
    if (domainFromEmail && !domainInitializedRef.current) {
      setOrganizationData((prev) => ({
        ...prev,
        email_domains: [domainFromEmail],
      }));
      domainInitializedRef.current = true;
    }
  }, [domainFromEmail]);

  const handleSelectOrganization = async (orgUuid: string) => {
    try {
      await dispatch(assignOrganization(orgUuid) as any);
    } catch (error) {
      console.error('Error selecting organization:', error);
    }
  };


  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organizationData.name || !organizationData.type || organizationData.email_domains.length === 0) {
      return;
    }
    try {
      await dispatch(
        createOrganization({
          name: organizationData.name,
          type: organizationData.type,
          email_domains: organizationData.email_domains,
          org_details: organizationData.org_details,
          active_license_count: organizationData.active_license_count,
        }) as any
      );
    } catch (error) {
      console.error('Error creating organization:', error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setOrganizationData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const hasMatchingOrganizations = onboarding.matchingOrganizations && onboarding.matchingOrganizations.length > 0;
  const hasExistingOrganization = onboarding.selectedOrganization?.id || onboarding.user?.organization_id;

  // If user already belongs to an organization, show confirmation (read-only)
  if (hasExistingOrganization) {
    const orgName = onboarding.selectedOrganization?.name || t('Your Organization');
    return (
      <OnboardingStepCard
        title={t('Confirm Your Organization')}
        description={t("You're assigned to this organization. Please confirm and continue.")}
        footer={
          <FormActions>
            <Button
              variant="primary"
              size="small"
              onClick={() => {
                dispatch({ type: 'ONBOARDING_SET_STEP', payload: 'user' });
              }}
            >
              {t('Confirm & Continue')}
            </Button>
          </FormActions>
        }
      >
        <Well className="flex items-center gap-3">
          <PiCheckCircleLight className="h-6 w-6 flex-shrink-0 text-success-500" />
          <p className="text-sm font-medium text-fg-strong">{orgName}</p>
        </Well>
      </OnboardingStepCard>
    );
  }

  // If we have matching organizations from domain check, show options to join
  if (hasMatchingOrganizations) {
    return (
      <OnboardingStepCard
        title={t('Join your organization')}
        description={t(
          '{{count}} organizations match your email domain.',
          { count: onboarding.matchingOrganizations.length },
        )}
      >
        <div className="space-y-2">
          {onboarding.matchingOrganizations.map((org) => (
            <Well
              key={org.id}
              className="flex items-center justify-between gap-3 transition-colors hover:border-champagne/40"
            >
              <p className="min-w-0 flex-1 truncate text-sm font-medium text-fg-strong">
                {org.name}
              </p>
              <Button
                variant="primary"
                size="small"
                onClick={() => handleSelectOrganization(org.uuid)}
                disabled={onboarding.isLoading}
                isLoading={onboarding.isLoading}
              >
                {t('Join Organization')}
              </Button>
            </Well>
          ))}
        </div>
      </OnboardingStepCard>
    );
  }

  // No matching organization - show creation form
  return (
    <OnboardingStepCard
      as="form"
      onSubmit={handleCreateOrganization}
      title={t('Create your organization')}
      description={t("Create a new organization to get started. You'll be set as the organization admin.")}
      footer={
        <FormActions>
          <Button
            type="submit"
            variant="primary"
            size="small"
            disabled={!organizationData.name || organizationData.email_domains.length === 0 || onboarding.isLoading}
            isLoading={onboarding.isLoading}
          >
            {t('Create Organization')}
          </Button>
        </FormActions>
      }
    >
      <Input
        type="text"
        label={t('Organization Name')}
        placeholder={t('e.g., Acme Law Firm')}
        value={organizationData.name}
        onChange={(e) => handleInputChange('name', e.target.value)}
        required
      />

      {/* Email Domains - Read-only, locked to signup domain */}
      <FieldShell
        label={t('Email Domain')}
        description={t('Your organization is locked to the email domain you signed up with.')}
        translate={false}
      >
        {organizationData.email_domains.length > 0 && (
          <Well className="flex items-center gap-2 !p-3">
            <PiCheckCircleLight className="h-5 w-5 flex-shrink-0 text-success-500" />
            <span className="text-sm font-medium text-fg-strong">
              {organizationData.email_domains[0]}
            </span>
          </Well>
        )}
      </FieldShell>

      {/* Optional Details Toggle */}
      <button
        type="button"
        onClick={() => setShowOrgDetails(!showOrgDetails)}
        aria-expanded={showOrgDetails}
        className={`flex items-center gap-1 rounded text-sm font-medium text-fg-body hover:underline ${FOCUS_RING_ONLY}`}
      >
        {showOrgDetails ? (
          <PiCaretDownLight className="h-4 w-4" />
        ) : (
          <PiCaretRightLight className="h-4 w-4" />
        )}
        {t('Additional Details (Optional)')}
      </button>

      {/* Org Details - Regions, Description */}
      {showOrgDetails && (
        <div className="space-y-4 border-t border-hairline-soft pt-4">
          <p className="text-xs text-fg-muted">
            {t('Add organizational details. You can update these later.')}
          </p>

          <TagInput
            id="regions-input"
            label={t('Regions')}
            placeholder={t("Write the regions, cities or countries you operate in, followed by ',' or enter")}
            tags={organizationData.org_details.regions}
            onChange={(regions) =>
              setOrganizationData((prev) => ({
                ...prev,
                org_details: {
                  ...prev.org_details,
                  regions,
                },
              }))
            }
          />

          <Input
            type="text"
            label={t('Description')}
            placeholder={t('e.g., Law firm specializing in corporate law')}
            value={organizationData.org_details.description}
            onChange={(e) =>
              setOrganizationData((prev) => ({
                ...prev,
                org_details: {
                  ...prev.org_details,
                  description: e.target.value,
                },
              }))
            }
          />
        </div>
      )}
    </OnboardingStepCard>
  );
};

export default OrganizationCheckStep;
